import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	SetDigitalTwinsPageNumber,
	SetDigitalTwinsTotal,
	SetDigitalTwinsListOfBatteries,
} from "../../Actions/DigitalTwinsList";
import { getDigitalTwinList, getDigitalTwinData } from "../../Api/DigitalTwinListApi";
import { SetSessionExpired } from "../../Actions";
import CODE from "../../Static/Constants/StatusCodes";
import { fetchFiltersDataObject } from "../../Helper/Table/TableHelper";
import {
	extractPartialConnectivityParams,
	getPartialConnectivityFilterKeys
} from "../../Helper/Filters/FilterTimeRangeExtractor";
import AppCache from "../../Helper/Common/AppCache";
import { ALL_VEHICLES_INDICATORS, withIndicatorSourceFields } from "../../Components/FieldStatusIndicator";
import GenericDraggableTableView from "../../Features/Table/GenericDraggableTable";
import { useTranslation } from 'react-i18next';
import { Box } from "@mui/material";
import { getOrganizationLangSettings } from "../../Helper/Organization/Functions";
import { MAX_COLUMN_SELECTION_COUNT } from "../../Helper/DigitalTwin/DataFile";
import {
	shouldDeferToPageReset,
	shouldWaitForGlobalFilter,
} from "../../Helper/Inventory/inventoryLoadHelper";
import { getAvailableEmailDomains } from "../../Helper/Organization/EmailDomains";
import OperationalStatusAction from "../../Components/OperationalStatus/OperationalStatusAction";
import { buildSelectAllFilters } from "../../Helper/Inventory/selectAllFilters";
import { usePermissions } from "../../Context/PermissionContext";
import { FEATURE_CODES } from "../../Static/FeatureMapping";
import { CanUpdate } from "../../Components/PermissionGuards";

const COLUMN_SETTING_STORAGE_KEY = "DIGITAL_TWIN_COLUMN_SETTINGS";
const TAB_INDEX = 0;
// Module key for the global filter headers (pre-applied exclusions are looked up by it).
const GLOBAL_FILTER_MODULE = 'inventory.digitalTwinListTable';

export default function DigitalTwinListTab({
	selectedFilters,
	setSelectedFilters,
	sorting,
	setSorting,
	searchTerm,
	setSearchTerm,
	visibleColumnKeys,
	setVisibleColumnKeys,
	pagesContent,
	permissions,
	navigate,
	columns: allColumns,
	connectedBatteries,
	onTagsUpdate,
	globalFilters,
	allFleets,
	allOnboardingLocationsHierarchy,
	globalFilterConfig,
	canUseGlobalAdvancedFilter,
	globalFilterReady,
	onGlobalFilterChange,
}) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { hasPermission } = usePermissions();
	const pageNumber = useSelector((state) => state.DigitalTwinsPageNumber.value);
	const rowsPerPage = useSelector((state) => state.DigitalTwinsRowsPerPage.value);
	const total = useSelector((state) => state.DigitalTwinsTotal.value);

	const [listOfBatteries, setListOfBatteries] = useState({
		batteries: [],
		responseStatus: { code: -1, msg: "" },
	});
	const [isLoading, setIsLoading] = useState(true);
	const [columns, setColumns] = useState([]);
	const [filters, setFilters] = useState({});
	const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
	const [totalSelectedRowBatteryIds, setTotalSelectedRowBatteryIds] = useState(new Set());
	const [totalSelectedRowVehicleNumbers, setTotalSelectedRowVehicleNumbers] = useState(new Set());
	const [selectedColumns, setSelectedColumns] = useState([]);
	const [isSelectAll, setIsSelectAll] = useState(false);
	const [columnOrder, setColumnOrder] = useState([]);
	const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([]);

	const lastFetchParams = useRef(null);
	// Global filters as the list request sends them. The select-all status update must
	// send exactly these, or the backend updates rows the list does not show.
	const listGlobalFilters = canUseGlobalAdvancedFilter ? (globalFilters || {}) : {};
	const disableDownloads = pagesContent?.disableDownloads == true;
	const tabColumns = allColumns?.[TAB_INDEX]?.columns ?? [];

	/**
	 * Columns to ask the API for.
	 *
	 * The list endpoint returns only the columns the request names, so a field status
	 * marker whose source column is not currently selected would have nothing to read -
	 * which is the common case, since users filter on Operational Status while showing
	 * other columns. The marker's source fields are appended here.
	 *
	 * Deliberately appended to the REQUEST only. visibleColumnKeys and the `columns`
	 * state stay untouched: the CSV and email paths serialise `columns`, so writing the
	 * extra keys into state would add a column the user never selected to the download.
	 */
	const getRequestColumns = useCallback(
		() => withIndicatorSourceFields(
			visibleColumnKeys[TAB_INDEX],
			ALL_VEHICLES_INDICATORS,
			pagesContent?.metaInfo
		),
		[visibleColumnKeys, pagesContent]
	);

	const [list, setList] = useState({
		batteries: {},
		pages: { start: 1, end: 0, total: 0 },
		responseStatus: { code: CODE.LOADING, msg: "" },
	});

	const getCurrentSorting = () => {
		if (sorting && sorting[TAB_INDEX]) {
			return sorting[TAB_INDEX];
		}
		return {};
	};

	const setupTabColumns = useCallback(() => {
		if (columns.length > 0) return;

		const defaultColumns = (() => {
			const sorted = tabColumns.sort((a, b) => {
				const order = col => {
					if (col.fixed === "left") return 0;
					if (col.fixed === "right") return 2;
					return 1;
				};
				return order(a) - order(b);
			});

			const sliced = sorted.slice(0, MAX_COLUMN_SELECTION_COUNT);
			const leftAndNone = sliced.filter(c => c.fixed !== "right");
			const rightOnly = sliced.filter(c => c.fixed === "right");

			return [...leftAndNone, ...rightOnly];
		})();

		const columnSettingKeys = AppCache.getItem(COLUMN_SETTING_STORAGE_KEY) || {};
		const columnSettingTabKeys = columnSettingKeys[TAB_INDEX] || [];

		const filteredColumns = columnSettingTabKeys.length
			? tabColumns.filter(({ dataIndex }) => columnSettingTabKeys.includes(dataIndex))
			: defaultColumns;

		const filteredColumnKeys = columnSettingTabKeys.length
			? columnSettingTabKeys
			: defaultColumns.map(({ dataIndex }) => dataIndex);

		setDefaultSelectedColumns(defaultColumns.map(({ dataIndex }) => dataIndex));
		setColumns(filteredColumns);
		setSelectedColumns(filteredColumnKeys);
		setVisibleColumnKeys((prev) => ({
			...prev,
			[TAB_INDEX]: filteredColumnKeys
		}));
	}, [columns, tabColumns, setVisibleColumnKeys]);

	const setFiltersAndData = async () => {
		setList(listOfBatteries);
		setIsLoading(false);

		const digitalTwinTableFilters = pagesContent?.filterConfigurations?.digitalTwinListTable?.filters;

		if (!digitalTwinTableFilters) {
			return false;
		}

		const tabFilters = digitalTwinTableFilters.filter((filter) => {
			if (!filter.tabs) return true;
			return filter.tabs.includes(TAB_INDEX);
		});

		const filtersObj = await fetchFiltersDataObject(tabFilters, filters);

		const sortedFiltersObj = Object.fromEntries(
			Object.entries(filtersObj).sort(([, a], [, b]) => a.label.localeCompare(b.label))
		);

		setFilters(sortedFiltersObj);
	};

	const applySort = (key, order) => {
		const tempSort = [];
		tempSort[TAB_INDEX] = { sortBy: key, orderBy: order };
		setSorting(tempSort);
	};

	const onPageChange = (page, pageSize) => {
		dispatch(SetDigitalTwinsPageNumber(page));
	};

	const getFilterConfigsMap = () => {
		const filtersConfig = pagesContent?.filterConfigurations?.digitalTwinListTable?.filters;
		if (!filtersConfig) return {};

		// Build a map of filter keys to their configurations
		// Also handle composite filters by extracting subfilters
		const configMap = {};

		filtersConfig.forEach(filter => {
			if (filter.displayType === 'compositeSelectFilter' && filter.subFilters) {
				// For composite filters, map each subfilter key to its config
				filter.subFilters.forEach(subFilter => {
					configMap[subFilter.key] = subFilter;
				});
			} else {
				// For regular filters
				configMap[filter.key] = filter;
			}
		});

		return configMap;
	};

	async function getDigitalTwinListData(params = {}) {
		const sortBy = sorting[TAB_INDEX]?.sortBy;
		const orderBy = sorting[TAB_INDEX]?.orderBy;
		const searchID = searchTerm?.selectedSearchColumn || 'searchID';

		// Get filter configurations for mapping IDs to display values
		const filterConfigsMap = getFilterConfigsMap();

		// Extract partial connectivity filter parameters
		const partialConnectivityParams = extractPartialConnectivityParams(
			selectedFilters,
			filterConfigsMap
		);

		// Get keys used for partial connectivity to exclude from regular filters
		const partialConnectivityKeys = getPartialConnectivityFilterKeys(selectedFilters);

		// Filter out partial connectivity keys from regular filters
		const regularFilters = Object.keys(selectedFilters).reduce((acc, key) => {
			if (!partialConnectivityKeys.includes(key)) {
				acc[key] = selectedFilters[key];
			}
			return acc;
		}, {});

		// DEBUG
		console.log('🔍 DigitalTwinListTab Debug:');
		console.log('  selectedFilters:', selectedFilters);
		console.log('  partialConnectivityKeys:', partialConnectivityKeys);
		console.log('  regularFilters:', regularFilters);
		console.log('  partialConnectivityParams:', partialConnectivityParams);

		let response = await getDigitalTwinList({
			filters: regularFilters,  // ← Excludes canDataTypes and canDataDateRange
			pageNumber,
			rowsPerPage,
			sortBy,
			orderBy,
			batteryID: null,
			startDate: null,
			endDate: null,
			[searchID]: searchTerm?.selectedDevices || [],
			columns: getRequestColumns(),
			// Add partial connectivity parameters
			...(partialConnectivityParams?.canDataTypes && {
				canDataTypes: partialConnectivityParams.canDataTypes,
			}),
			...(partialConnectivityParams?.canDataDateRange && {
				canDataDateRange: partialConnectivityParams.canDataDateRange,
			}),
			...params
		}, listGlobalFilters, GLOBAL_FILTER_MODULE);

		return {
			responseCode: response.responseStatus.code,
			total: response.response.total?.total,
			data: response?.response?.data || [],
			responseStatus: response.responseStatus
		};
	}

	const setTotalRowKeysForSelectAll = async () => {
		let ids = [];
		const response = await getDigitalTwinListData({ allIdsOnly: true });

		if (response && response.responseStatus.code === 200) {
			ids = response.data;
		}

		if (ids.length > 0) {
			setTotalSelectedRowKeys(new Set(ids));
		}
	};

	const onSelectChange = (record, selected, selectedKeys) => {
		const updatedSelectedKeys = new Set(totalSelectedRowKeys);
		const updatedSelectedBatteryIds = new Set(totalSelectedRowBatteryIds);
		const updatedSelectedVehicleNumbers = new Set(totalSelectedRowVehicleNumbers);

		if (selected) {
			updatedSelectedKeys.add(record.id);
			updatedSelectedBatteryIds.add(record.batteryID);
			updatedSelectedVehicleNumbers.add(record?.licensePlateNo);
		} else {
			updatedSelectedKeys.delete(record.id);
			updatedSelectedBatteryIds.delete(record.batteryID);
			updatedSelectedVehicleNumbers.delete(record?.licensePlateNo);
			if (isSelectAll) {
				setIsSelectAll(false);
			}
		}

		setTotalSelectedRowKeys(updatedSelectedKeys);
		setTotalSelectedRowBatteryIds(updatedSelectedBatteryIds);
		setTotalSelectedRowVehicleNumbers(updatedSelectedVehicleNumbers);
	};

	const onSelectAllChange = (selected, selectedRows, changeRows) => {
		setIsSelectAll(selected);
		if (selected) {
			setTotalSelectedRowKeys(new Set(selectedRows.map((row) => row?.id)));
			setTotalSelectedRowBatteryIds(new Set(selectedRows.map((row) => row?.batteryID)));
			setTotalSelectedRowVehicleNumbers(new Set(selectedRows.map((row) => row?.licensePlateNo)));
			setTotalRowKeysForSelectAll();
			return;
		}

		setTotalSelectedRowKeys(new Set());
		setTotalSelectedRowBatteryIds(new Set());
		setTotalSelectedRowVehicleNumbers(new Set());
		return;
	};

	const getTotalRowSelection = () => {
		return Array.from(totalSelectedRowKeys);
	};

	const getTotalRowSelectionCount = () => {
		if (isSelectAll) {
			return list?.pages?.total;
		}
		return totalSelectedRowKeys.size;
	};

	const getCurrentPageSelection = () => {
		return Array.from(totalSelectedRowKeys);
	};

	const getCurrentBatteryIdsSelection = () => {
		return Array.from(totalSelectedRowBatteryIds);
	};

	const getCurrentVehicleNumbersSelection = () => {
		return Array.from(totalSelectedRowVehicleNumbers);
	};

	const filtersForSelectAll = () => buildSelectAllFilters({
		selectedFilters,
		searchTerm,
		filterConfigsMap: getFilterConfigsMap(),
	});

	const getCSVData = async () => {
		const res = await getDigitalTwinData({
			resourceIDs: getTotalRowSelection(),
			actionType: "download-csv",
			filters: filtersForSelectAll(),
			resourceType: "digital-twin",
			columnOrder: columnOrder,
			columns: JSON.stringify(columns)
		});

		if (res && res.responseStatus.code === 200) {
			return res.response.data;
		}

		if (res && res.responseStatus.code === -2) dispatch(SetSessionExpired(true));
		return [];
	};

	const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
		const res = await getDigitalTwinData({
			resourceIDs: getTotalRowSelection(),
			filters: filtersForSelectAll(),
			actionType: "mail",
			resourceType: "digital-twin",
			columnOrder: columnOrder,
			columns: JSON.stringify(columns),
			emailTo: sendTo,
			emailCc: sendCc,
			subject: subject,
			body: body
		});

		if (res && res.responseStatus.code === 200) {
			return res.response.data;
		}

		if (res && res.responseStatus.code === -2) dispatch(SetSessionExpired(true));
		return [];
	};

	const applySelectedColumns = (newSelectedColumns) => {
		const filteredColumns = tabColumns.filter((columnConfig) => {
			return newSelectedColumns.includes(columnConfig.dataIndex);
		});

		setColumns(filteredColumns);
		setSelectedColumns(newSelectedColumns);

		const updatedSelectedColumns = { ...AppCache.getItem(COLUMN_SETTING_STORAGE_KEY) || {} };
		updatedSelectedColumns[TAB_INDEX] = newSelectedColumns;
		AppCache.setItem(COLUMN_SETTING_STORAGE_KEY, updatedSelectedColumns);

		setVisibleColumnKeys((prev) => ({
			...prev,
			[TAB_INDEX]: newSelectedColumns
		}));

		setColumnOrder(filteredColumns.map((col) => col.headerLabel || col.title));
	};

	const handlePrimaryActionButtonClick = () => {
		return navigate("/DigitalTwin/add-battery");
	};

	const shouldShowAddResourcesButton = () => {
		const rbacVersion = AppCache.getItem('rbacVersion') || 'v1';
		const rbacEnabled = AppCache.getItem('rbacEnabled') ?? false;

		// V2: Only check RBAC permissions
		if (rbacVersion === 'v2' && rbacEnabled) {
			return hasPermission(FEATURE_CODES.DIGITAL_TWIN, 'create');
		}

		// V1: Check old feature flag system
		const enabledFeatures = pagesContent?.enabledFeatures ?? [];
		return enabledFeatures.includes('Inventory.AddDigitalTwin');
	};

	const onColumnOrderChange = (tableColumns) => {
		setColumnOrder(tableColumns.map((value) => value.headerLabel));
	};

	const shouldShowFilteredCount = () => {
		if (isLoading) {
			return false;
		}
		return (searchTerm && searchTerm.selectedDevices?.length > 0) || Object.keys(selectedFilters || {}).length > 0;
	};

	const handleClearSelection = () => {
		setTotalSelectedRowKeys(new Set());
		setTotalSelectedRowBatteryIds(new Set());
		setTotalSelectedRowVehicleNumbers(new Set());
		setIsSelectAll(false);
	};

	async function GetListOfBatteries() {
		if (listOfBatteries.responseStatus.code !== -1) {
			setListOfBatteries({
				batteries: [],
				responseStatus: { code: -1, msg: "" },
			});
		}

		setIsLoading(true);
		const sortBy = sorting[TAB_INDEX]?.sortBy;
		const orderBy = sorting[TAB_INDEX]?.orderBy;
		const searchID = searchTerm?.selectedSearchColumn || 'searchID';

		// Get filter configurations for mapping IDs to display values
		const filterConfigsMap = getFilterConfigsMap();

		// Extract partial connectivity filter parameters
		const partialConnectivityParams = extractPartialConnectivityParams(
			selectedFilters,
			filterConfigsMap
		);

		// Get keys used for partial connectivity to exclude from regular filters
		const partialConnectivityKeys = getPartialConnectivityFilterKeys(selectedFilters);

		// Filter out partial connectivity keys from regular filters
		const regularFilters = Object.keys(selectedFilters).reduce((acc, key) => {
			if (!partialConnectivityKeys.includes(key)) {
				acc[key] = selectedFilters[key];
			}
			return acc;
		}, {});

		await getDigitalTwinList({
			filters: regularFilters,  // ← Excludes canDataTypes and canDataDateRange
			pageNumber,
			rowsPerPage,
			sortBy,
			orderBy,
			batteryID: null,
			startDate: null,
			endDate: null,
			[searchID]: searchTerm?.selectedDevices || [],
			columns: getRequestColumns(),
			// Add partial connectivity parameters
			...(partialConnectivityParams?.canDataTypes && {
				canDataTypes: partialConnectivityParams.canDataTypes,
			}),
			...(partialConnectivityParams?.canDataDateRange && {
				canDataDateRange: partialConnectivityParams.canDataDateRange,
			}),
		}, listGlobalFilters, GLOBAL_FILTER_MODULE).then((res) => {
			if (res.responseStatus.code === 200) {
				setListOfBatteries({
					batteries: res.response.data,
					responseStatus: res.response.responseStatus,
					pages: res.response.total
				});
				dispatch(SetDigitalTwinsListOfBatteries(res.response.data));
				dispatch(SetDigitalTwinsTotal(res.response.total?.total));
			} else {
				if (res.responseStatus.code === -2) dispatch(SetSessionExpired(true));
				setListOfBatteries({
					batteries: [],
					pages: res.response.total,
					responseStatus: {
						code: res.responseStatus?.code,
						msg: res.responseStatus?.msg,
					},
				});
				dispatch(SetDigitalTwinsTotal(0));
			}
			setIsLoading(false);
		});
	}

	useEffect(() => {
		setupTabColumns();
	}, [tabColumns, setupTabColumns]);

	useEffect(() => {
		if (!visibleColumnKeys[TAB_INDEX]?.length) {
			return;
		}

		// Hold the first fetch until the fleet/depot scope has loaded, or the list fires
		// twice -- the first time WITHOUT scope headers. See shouldWaitForGlobalFilter.
		if (shouldWaitForGlobalFilter(canUseGlobalAdvancedFilter, globalFilterReady)) {
			return;
		}

		const currentParams = {
			pageNumber,
			selectedFilters: JSON.stringify(selectedFilters),
			searchTerm: JSON.stringify(searchTerm),
			sorting: JSON.stringify(sorting[TAB_INDEX]),
			visibleColumnKeys: JSON.stringify(visibleColumnKeys[TAB_INDEX]),
			globalFilters: canUseGlobalAdvancedFilter ? JSON.stringify(globalFilters) : ''
		};

		// Stand down if the page-reset effect below is about to fire us again with page 1.
		// Deliberately does NOT record currentParams: the follow-up run has to still see
		// them as changed, or it would fetch nothing at all.
		if (shouldDeferToPageReset(lastFetchParams.current, currentParams, pageNumber)) {
			return;
		}

		const paramsChanged = JSON.stringify(currentParams) !== JSON.stringify(lastFetchParams.current);

		if (paramsChanged) {
			lastFetchParams.current = currentParams;
			GetListOfBatteries();
		}
	}, [pageNumber, selectedFilters, searchTerm, sorting, visibleColumnKeys, globalFilters, globalFilterReady]);

	useEffect(() => {
		if (pageNumber !== 1) {
			dispatch(SetDigitalTwinsPageNumber(1));
		}
	}, [selectedFilters, searchTerm, sorting[TAB_INDEX], globalFilters]);

	useEffect(() => {
		if (listOfBatteries.responseStatus.code != CODE.LOADING) {
			setFiltersAndData();
		}
	}, [listOfBatteries]);

	const tableData = list.responseStatus.code === CODE.SUCCESS ? list.batteries : [];
	const orgLangSettings = getOrganizationLangSettings();

	return (
		<Box>
		<GenericDraggableTableView
			tableID='DigitalTwinList'
			title={t(orgLangSettings.digitalTwinLabel)}
			count={connectedBatteries}
			primaryButtonLabel={t(orgLangSettings.digitalTwinAddResource)}
			showPrimaryButton={shouldShowAddResourcesButton()}
			onPrimaryButtonClick={() => handlePrimaryActionButtonClick()}
			applySearch={(selectedDevices, selectedSearchColumn) => setSearchTerm({ selectedDevices, selectedSearchColumn })}
			searchableFields={pagesContent?.searchableFields || undefined}
			disableSearch={false}
			disableDownloads={disableDownloads}
			getTotalRowSelection={getTotalRowSelection}
			getTotalRowSelectionCount={getTotalRowSelectionCount}
			getCSVData={getCSVData}
			onSendEmail={onSendEmail}
			availableEmailDomains={getAvailableEmailDomains(pagesContent)}
			filters={filters}
			selectedFilters={selectedFilters}
			applyFilters={(args) => setSelectedFilters(args)}
			initialColumns={tabColumns}
			selectedColumns={selectedColumns}
			applySelectedColumns={applySelectedColumns}
			filteredCount={shouldShowFilteredCount() ? list?.pages?.total : 0}
			tableColumns={columns}
			tableData={tableData}
			onColumnOrderChange={onColumnOrderChange}
			columnOrder={columnOrder}
			isLoading={isLoading}
			pageNumber={pageNumber}
			rowsPerPage={rowsPerPage}
			total={total}
			applySort={applySort}
			onPageChange={onPageChange}
			onClearSelection={() => handleClearSelection()}
			emptyMessage="No matches found for filters applied"
			customRightActions={true}
			initialSorting={getCurrentSorting()}
			customRightActionsView={(
				<CanUpdate feature={FEATURE_CODES.OPERATIONAL_STATUS}>
					<Box sx={{ ml: 1 }}>
						<OperationalStatusAction
							entityIds={getCurrentBatteryIdsSelection()}
							isInventoryPage={true}
							isActive={getCurrentBatteryIdsSelection() && getCurrentBatteryIdsSelection().length > 0}
							entityType={'battery'}
							onUpdate={() => {
								onTagsUpdate();
								// None of the list's fetch triggers change on a status update, so
								// refetch the current page here or the column keeps the old status.
								// The selection is cleared so it cannot be reused against stale rows.
								handleClearSelection();
								GetListOfBatteries();
							}}
							selectedFilters={filtersForSelectAll()}
							isSelectAll={isSelectAll}
							globalFilters={listGlobalFilters}
							globalFilterModule={GLOBAL_FILTER_MODULE}
							totalCount={list?.pages?.total}
							vehicleNumber={getCurrentVehicleNumbersSelection().length === 1 ? getCurrentVehicleNumbersSelection()[0] : ''}
							deviceID={getCurrentBatteryIdsSelection().length === 1 ? getCurrentBatteryIdsSelection()[0] : ''}
						/>
					</Box>
				</CanUpdate>
			)}
			maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
			defaultSelectedColumns={defaultSelectedColumns}
			rowSelection={{
				selectedRowKeys: getCurrentPageSelection(),
				onSelect: onSelectChange,
				onSelectAll: onSelectAllChange
			}}
		/>
		</Box>
	);
}
